import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  LineChart,
  Package,
  ShoppingCart,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import {
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '@/lib/seo';
import { trackCTA, trackServiceView } from '@/lib/analytics';
import { trackScroll } from '@/lib/scroll';
import { getServiceMeta } from '@/seo/meta';

const serviceName = 'Κατασκευή E-Shop';
const serviceSlug = 'ecommerce';

type Locale = 'en' | 'el';

type Metric = {
  value: string;
  label: string;
};

type HeroCardContent = {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  contactLabel: string;
};

type HeroContent = {
  eyebrow: string;
  leading: string;
  accent?: string;
  accentLines?: string[];
  description: string;
  highlights: string[];
  metrics: Metric[];
  primaryCta: string;
  secondaryCta: string;
  card: HeroCardContent;
};

type PillarKey = 'conversion' | 'experience' | 'operations' | 'growth';

type PillarCopy = {
  title: string;
  description: string;
  bullets: string[];
};

type ProcessStep = {
  title: string;
  description: string;
  deliverable?: string;
  duration?: string;
  bullets?: string[];
};

type ClosingContent = {
  title: string;
  description: string;
  highlights: string[];
  primaryCta: string;
  secondaryCta: string;
};

type PortfolioCopy = {
  eyebrow: string;
  heading: string;
  description: string;
  button: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'E-commerce Services',
    leading: 'E-commerce stores designed for sales and scale.',
    description:
      'We design and build e-commerce stores that run fast, handle demand, and turn visitors into customers, with a focus on brand and performance.',
    highlights: [],
    metrics: [
      { value: '4–8 weeks', label: 'Average launch time' },
      { value: '+31%', label: 'Typical revenue lift' },
      { value: '99.9%', label: 'Uptime & monitoring' },
    ],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See e-commerce work',
    card: {
      eyebrow: 'What ships',
      title: 'Commerce foundations delivered from day one',
      description:
        'Every launch combines merchandising strategy, high-performing UX, and operational automation so trading never slows down.',
      bullets: [
        'SKU & catalog architecture with cross-sell playbooks',
        'Checkout UX, payment flows, and tax/shipping automation',
        'Analytics dashboards, forecasting templates, and training',
      ],
      contactLabel: 'Need an immediate call?',
    },
  },
  el: {
    eyebrow: 'Υπηρεσίες E-commerce',
    leading: 'E-shop σχεδιασμένα για πωλήσεις και κλιμάκωση.',
    description:
      'Σχεδιάζουμε και υλοποιούμε e-shop που λειτουργούν γρήγορα, αντέχουν στη ζήτηση και μετατρέπουν επισκέπτες σε πελάτες, με έμφαση στο brand και την απόδοση.',
    highlights: [],
    metrics: [
      { value: '4–8 εβδομάδες', label: 'Μέσος χρόνος launch' },
      { value: '+31%', label: 'Τυπική αύξηση εσόδων' },
      { value: '99.9%', label: 'Διαθεσιμότητα & monitoring' },
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα e-commerce',
    card: {
      eyebrow: 'Τι παραδίδουμε',
      title: 'Commerce θεμέλια έτοιμα από την πρώτη μέρα',
      description:
        'Κάθε λανσάρισμα συνδυάζει στρατηγική merchandising, UX υψηλής απόδοσης και αυτοματισμούς λειτουργίας για να μην πέφτουν οι ρυθμοί.',
      bullets: [
        'Αρχιτεκτονική καταλόγου & playbooks για cross-sell',
        'Checkout UX, πληρωμές και αυτοματοποίηση φόρων/μεταφορικών',
        'Dashboards analytics, forecasting templates και εκπαίδευση',
      ],
      contactLabel: 'Θέλετε άμεση επικοινωνία;',
    },
  },
};

const approachCopy: Record<
  Locale,
  {
    eyebrow: string;
    heading: string;
    description: string;
  }
> = {
  en: {
    eyebrow: 'Our approach',
    heading: 'E-commerce stores with a solid technical foundation and clean UX.',
    description:
      'We focus first on structure, performance, and maintainability so the store is stable today and ready to scale tomorrow.',
  },
  el: {
    eyebrow: 'Η προσέγγισή μας',
    heading: 'E-shop με σωστή τεχνική βάση και καθαρή εμπειρία χρήστη.',
    description:
      'Δουλεύουμε πρώτα τη δομή, την απόδοση και τη συντηρησιμότητα, ώστε το κατάστημα να είναι σταθερό σήμερα και έτοιμο να επεκταθεί αύριο.',
  },
};

const pillarBase: Array<{ key: PillarKey; icon: LucideIcon }> = [
  { key: 'conversion', icon: ShoppingCart },
  { key: 'experience', icon: LayoutDashboard },
  { key: 'operations', icon: Package },
  { key: 'growth', icon: LineChart },
] as const;

const pillarCopy: Record<Locale, Record<PillarKey, PillarCopy>> = {
  en: {
    conversion: {
      title: 'Clear structure before development',
      description:
        'We start with clear organization so the store does not get patched together later.',
      bullets: [
        'Category and product structure that makes sense',
        'Simple buying flow without unnecessary steps',
        'Content review and prioritization before any code is written',
      ],
    },
    experience: {
      title: 'Design that supports usage',
      description:
        'Design serves function, not the other way around.',
      bullets: [
        'Consistent look across home, categories, and product pages',
        'Mobile- and tablet-friendly design',
        'Clear messaging and readable content',
      ],
    },
    operations: {
      title: 'Stable and fast implementation',
      description:
        'We build stores that load fast and do not break with the first change.',
      bullets: [
        'Optimized code for speed and reliability',
        'Secure payments and proper checkout function',
        'Quality checks before and after launch',
      ],
    },
    growth: {
      title: 'Control and evolution after launch',
      description:
        'The store is delivered so you can manage it without dependency.',
      bullets: [
        'Training on product and content management',
        'Basic traffic and sales statistics',
        'Clear plan for next improvements when you are ready',
      ],
    },
  },
  el: {
    conversion: {
      title: 'Σωστή δομή πριν την υλοποίηση',
      description:
        'Ξεκινάμε με ξεκάθαρη οργάνωση για να μη «μπαλώνεται» το e-shop στην πορεία.',
      bullets: [
        'Δομή κατηγοριών και προϊόντων που βγάζει νόημα',
        'Ροή αγορών απλή, χωρίς περιττά βήματα',
        'Έλεγχος περιεχομένου και ιεράρχηση πριν γραφτεί κώδικας',
      ],
    },
    experience: {
      title: 'Σχεδιασμός που υποστηρίζει τη χρήση',
      description:
        'Ο σχεδιασμός υπηρετεί τη λειτουργία και όχι το αντίστροφο.',
      bullets: [
        'Συνεπής εικόνα σε αρχική, κατηγορίες και σελίδες προϊόντων',
        'Σχεδιασμός φιλικός σε κινητό και tablet',
        'Καθαρά μηνύματα και ευανάγνωστο περιεχόμενο',
      ],
    },
    operations: {
      title: 'Σταθερή και γρήγορη υλοποίηση',
      description:
        'Χτίζουμε e-shop που φορτώνουν γρήγορα και δεν «σπάνε» με την πρώτη αλλαγή.',
      bullets: [
        'Βελτιστοποιημένος κώδικας για ταχύτητα και αξιοπιστία',
        'Ασφαλείς πληρωμές και σωστή λειτουργία checkout',
        'Έλεγχος ποιότητας πριν και μετά το λανσάρισμα',
      ],
    },
    growth: {
      title: 'Έλεγχος και εξέλιξη μετά το launch',
      description:
        'Το e-shop παραδίδεται ώστε να μπορείτε να το διαχειρίζεστε χωρίς εξάρτηση.',
      bullets: [
        'Εκπαίδευση στη διαχείριση προϊόντων και περιεχομένου',
        'Βασικά στατιστικά επισκεψιμότητας και αγορών',
        'Σαφές πλάνο για επόμενες βελτιώσεις όταν είστε έτοιμοι',
      ],
    },
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Understanding & organization',
      description:
        'We learn the product, audience, and e-commerce needs before development starts.',
      bullets: [
        'Project goals and key improvement points',
        'Category and product structure',
        'Agreement on the delivery plan',
      ],
      duration: 'Week 1',
    },
    {
      title: 'Experience & visual design',
      description:
        'We design how users move through the store and how products are presented.',
      bullets: [
        'Shopping flow and product pages',
        'Design for mobile and desktop',
        'Templates for offers and key sections',
      ],
      duration: 'Weeks 1–2',
    },
    {
      title: 'Build & configuration',
      description:
        'We build the store and configure the core functions.',
      bullets: [
        'Store build',
        'Payment and shipping setup',
        'Quality checks before launch',
      ],
      duration: 'Weeks 2–5',
    },
    {
      title: 'Launch & support',
      description:
        'The store goes live and we show you how to manage it.',
      bullets: [
        'Content migration and final checks',
        'Training on store management',
        'Plan for next improvements',
      ],
      duration: 'Weeks 5–8',
    },
  ],
  el: [
    {
      title: 'Κατανόηση & οργάνωση',
      description:
        'Μαθαίνουμε το προϊόν, το κοινό και τις ανάγκες του e-shop πριν ξεκινήσει η κατασκευή.',
      bullets: [
        'Στόχοι έργου και βασικά σημεία βελτίωσης',
        'Δομή κατηγοριών και προϊόντων',
        'Συμφωνία στο πλάνο υλοποίησης',
      ],
      duration: 'Εβδομάδα 1',
    },
    {
      title: 'Σχεδιασμός εμπειρίας & εικόνας',
      description:
        'Σχεδιάζουμε πώς θα κινείται ο χρήστης και πώς θα παρουσιάζονται τα προϊόντα.',
      bullets: [
        'Ροή αγορών και σελίδες προϊόντων',
        'Σχεδιασμός για κινητό και υπολογιστή',
        'Πρότυπα για προσφορές και βασικές ενότητες',
      ],
      duration: 'Εβδομάδες 1–2',
    },
    {
      title: 'Κατασκευή & ρυθμίσεις',
      description:
        'Χτίζουμε το e-shop και ρυθμίζουμε τις βασικές λειτουργίες.',
      bullets: [
        'Κατασκευή του καταστήματος',
        'Ρύθμιση πληρωμών και αποστολών',
        'Έλεγχος σωστής λειτουργίας πριν το άνοιγμα',
      ],
      duration: 'Εβδομάδες 2–5',
    },
    {
      title: 'Λανσάρισμα & υποστήριξη',
      description:
        'Το e-shop ανεβαίνει live και σας δείχνουμε πώς να το διαχειρίζεστε.',
      bullets: [
        'Μεταφορά περιεχομένου και τελικοί έλεγχοι',
        'Εκπαίδευση στη διαχείριση του καταστήματος',
        'Πλάνο για επόμενες βελτιώσεις',
      ],
      duration: 'Εβδομάδες 5–8',
    },
  ],
};

const processIntro: Record<
  Locale,
  {
    eyebrow: string;
    heading: string;
    description: string;
  }
> = {
  en: {
    eyebrow: 'Operational model',
    heading: 'Step-by-step e-commerce delivery without risk.',
    description:
      'Clear process, shared visibility of progress, and practical deliverables at every stage.',
  },
  el: {
    eyebrow: 'Λειτουργικό μοντέλο',
    heading: 'Βήμα–βήμα υλοποίηση e-shop χωρίς ρίσκο.',
    description:
      'Ξεκάθαρη διαδικασία, κοινή εικόνα προόδου και πρακτικά παραδοτέα σε κάθε στάδιο.',
  },
};

const faqCopy: Record<
  Locale,
  {
    question: string;
    answer: string;
  }[]
> = {
  en: [
    {
      question: 'How long does it usually take to build an e-shop?',
      answer:
        'Usually 4 to 8 weeks. The time depends on the number of products, features, and whether there is an existing store to migrate.',
    },
    {
      question: 'Which platforms and technologies do you work with?',
      answer:
        'We mainly work with WooCommerce and modern solutions based on WordPress. The choice is always based on your needs and the size of your business.',
    },
    {
      question: 'Can you migrate products, customers, and orders from our existing store?',
      answer:
        'Yes. We can migrate products, categories, and essential data from your old store so nothing is lost in the transition.',
    },
    {
      question: 'Do you configure payments, shipping, and tax rules for us?',
      answer:
        'Yes. We set up payment methods, shipping, and basic tax rules so the store works properly from day one.',
    },
    {
      question: 'Do you provide ongoing optimisation and support?',
      answer:
        'Yes. After launch we can provide support, small improvements, and technical checks so the store stays stable and up to date.',
    },
  ],
  el: [
    {
      question: 'Πόσο διαρκεί συνήθως η κατασκευή ενός e-shop;',
      answer:
        'Συνήθως από 4 έως 8 εβδομάδες. Ο χρόνος εξαρτάται από τον αριθμό προϊόντων, τις λειτουργίες και το αν υπάρχει υπάρχον κατάστημα για μεταφορά.',
    },
    {
      question: 'Με ποιες πλατφόρμες και τεχνολογίες δουλεύετε;',
      answer:
        'Δουλεύουμε κυρίως με WooCommerce και σύγχρονες λύσεις που βασίζονται σε WordPress. Η επιλογή γίνεται πάντα με βάση τις ανάγκες και το μέγεθος της επιχείρησής σας.',
    },
    {
      question: 'Μπορείτε να μεταφέρετε προϊόντα, πελάτες και παραγγελίες από το υπάρχον κατάστημα;',
      answer:
        'Ναι. Μπορούμε να μεταφέρουμε προϊόντα, κατηγορίες και βασικά δεδομένα από το παλιό σας e-shop, ώστε να μη χαθεί τίποτα στην αλλαγή.',
    },
    {
      question: 'Ρυθμίζετε εσείς πληρωμές, αποστολές και φορολογία;',
      answer:
        'Ναι. Ρυθμίζουμε τρόπους πληρωμής, αποστολές και βασικούς φορολογικούς κανόνες ώστε το κατάστημα να λειτουργεί σωστά από την πρώτη μέρα.',
    },
    {
      question: 'Προσφέρετε συνεχή βελτιστοποίηση και υποστήριξη;',
      answer:
        'Ναι. Μετά το λανσάρισμα μπορούμε να αναλάβουμε υποστήριξη, μικρές βελτιώσεις και τεχνικό έλεγχο ώστε το e-shop να παραμένει σταθερό και ενημερωμένο.',
    },
  ],
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    title: 'Shall we start your e-shop?',
    description:
      'We discuss your needs and propose the next steps.',
    highlights: [],
    primaryCta: 'Free consultation',
    secondaryCta: 'See our services',
  },
  el: {
    title: 'Να ξεκινήσουμε το e-shop σας;',
    description:
      'Μιλάμε για τις ανάγκες σας και σας προτείνουμε τα επόμενα βήματα.',
    highlights: [],
    primaryCta: 'Δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε τις υπηρεσίες μας',
  },
};

const portfolioCopy: Record<Locale, PortfolioCopy> = {
  en: {
    eyebrow: 'Selected work',
    heading: 'E-commerce stores that deliver results.',
    description:
      'See our work with businesses that sell online with clear structure and stable performance.',
    button: 'View full portfolio',
  },
  el: {
    eyebrow: 'Επιλεγμένα έργα',
    heading: 'E-shop που φέρνουν αποτέλεσμα.',
    description:
      'Δείτε έργα μας από επιχειρήσεις που πωλούν online με σωστή δομή και σταθερή λειτουργία.',
    button: 'Δείτε όλο το portfolio',
  },
};

export default function Ecommerce() {
  const location = useLocation();
  const { language } = useLanguage();
  const localizedPath = useLocalizedPath();
  const locale = (language ?? 'el') as Locale;
  const metaData = getServiceMeta(serviceSlug, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const serviceDisplayName = locale === 'en' ? 'E-commerce Development' : serviceName;
  const hero = heroCopy[locale];
  const approach = approachCopy[locale];
  const pillars = pillarCopy[locale];
  const processIntroCopy = processIntro[locale];
  const processSteps = processCopy[locale];
  const faqItems = faqCopy[locale];
  const closing = closingCopy[locale];
  const portfolio = portfolioCopy[locale];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackServiceView(serviceSlug, language);
    const cleanup = trackScroll({ threshold: 0.75, once: true });
    return cleanup;
  }, [language]);

  const handleCTA = (ctaText: string) => {
    trackCTA(ctaText, window.location.pathname);
    window.navigateToConsultation?.();
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <>
      <Seo
        title={metaData.title}
        description={metaData.description}
        url={canonicalUrl}
        image={metaData.image}
        hreflang={hreflang}
      />

      <JsonLd json={generateOrganizationSchema()} />
      <JsonLd json={generateWebsiteSchema(locale)} />
      <JsonLd json={generateBreadcrumbSchema(serviceDisplayName, serviceSlug, locale)} />
      <JsonLd json={generateServiceSchema(serviceDisplayName, metaData.description, canonicalUrl, 'Ecommerce')} />
      <JsonLd json={generateFAQSchema(faqItems)} />

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10 overflow-hidden">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black"
              aria-hidden="true"
            />
            <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-3xl space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                    {hero.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {hero.leading}
                  </Display>
                </div>

                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {hero.description}
                </Display>

                {hero.highlights.length ? (
                  <ul className="space-y-3 text-sm text-white/70">
                    {hero.highlights.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70" />
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    onClick={() => handleCTA(hero.primaryCta)}
                  >
                    {hero.primaryCta}
                  </Button>
                  <Button
                    as="a"
                    href="#commerce-work"
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {hero.secondaryCta}
                  </Button>
                </div>

                <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
                  {hero.metrics.map((metric) => (
                    <div key={metric.label} className="space-y-1 py-2 sm:px-6">
                      <p className="text-base font-semibold tracking-tight text-white md:text-base">{metric.value}</p>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">{metric.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Container>
          </HeroSection>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {approach.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{approach.heading}</h2>
                <p className="text-lg text-white/70">{approach.description}</p>
              </div>

              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {pillarBase.map(({ key, icon: Icon }) => {
                  const copy = pillars[key];
                  return (
                    <div
                      key={key}
                      className="group relative overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80 transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="mt-6 text-2xl font-semibold text-white">{copy.title}</h3>
                      <p className="mt-3 text-sm text-white/70">{copy.description}</p>
                      <ul className="mt-6 space-y-3 text-sm text-white/75">
                        {copy.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-3">
                            <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                            <span className="leading-relaxed">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {processIntroCopy.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntroCopy.heading}</h2>
                <p className="text-lg text-white/70">{processIntroCopy.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-2">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <div>
                        {step.duration ? (
                          <p className="text-xs uppercase tracking-[0.3em] text-white/50">{step.duration}</p>
                        ) : null}
                        <h3 className={step.duration ? 'mt-1 text-2xl font-semibold text-white' : 'text-2xl font-semibold text-white'}>
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    {step.bullets?.length ? (
                      <ul className="mt-4 space-y-2 text-sm text-white/70">
                        {step.bullets.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : step.deliverable ? (
                      <div className="mt-auto flex items-center gap-2 text-sm text-white/65 transition group-hover:text-white/80">
                        <CheckCircle className="h-4 w-4 text-brand-highlight/70 transition group-hover:text-brand-highlight" />
                        <span>{step.deliverable}</span>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section id="commerce-work">
            <Container>
              <div className="mb-12 grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {portfolio.eyebrow}
                  </p>
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{portfolio.heading}</h2>
                  <p className="max-w-2xl text-white/70">{portfolio.description}</p>
                </div>
                <Button
                  as="a"
                  href={localizedPath('/projects')}
                  variant="secondary"
                  size="lg"
                  className="w-full justify-center md:w-auto md:justify-self-end"
                >
                  {portfolio.button}
                </Button>
              </div>
            </Container>
          </Section>

          <Section>
            <Container className="max-w-5xl">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {locale === 'en' ? 'FAQ' : 'FAQ'}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-4xl">
                  {locale === 'en' ? 'Answers to common e-commerce questions.' : 'Απαντήσεις στις συχνές απορίες για το e-commerce.'}
                </h2>
                <p className="mt-4 text-lg text-white/70">
                  {locale === 'en'
                    ? 'Still have questions? Let’s discuss technology, logistics, and growth goals together.'
                    : 'Έχετε κι άλλες απορίες; Ας συζητήσουμε την τεχνολογία, τα logistics και τους στόχους ανάπτυξης μαζί.'}
                </p>
              </div>

              <div className="mt-12 space-y-4">
                {faqItems.map((faq, index) => {
                  const open = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
                      >
                        <span>{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-white/60" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-white/60" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-white/10 px-6 py-5 text-sm text-white/70">
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-purple-500/20 blur-3xl opacity-60" />
                <div className="relative space-y-10">
                  <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl px-8 py-6 text-center hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10">
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                      {locale === 'en' ? 'Partnership' : 'Συνεργασία'}
                    </p>
                    <Display
                      variant="h1"
                      as="h2"
                      className="text-3xl font-semibold text-white md:text-5xl"
                    >
                      {closing.title}
                    </Display>
                  </div>

                  <p className="mx-auto max-w-3xl text-center text-lg text-white/70">{closing.description}</p>

                  {closing.highlights.length ? (
                    <ul className="mx-auto grid gap-3 text-left text-sm text-white/70 sm:grid-cols-3 sm:gap-4">
                      {closing.highlights.map((highlight) => (
                        <li
                          key={highlight}
                          className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                        >
                          <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition group-hover:bg-brand-highlight" />
                          <span className="leading-relaxed">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="flex flex-col justify-center gap-4 pt-2 sm:flex-row">
                    <Button variant="primary" size="lg" onClick={() => handleCTA(closing.primaryCta)}>
                      {closing.primaryCta}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/services')}
                      variant="secondary"
                      size="lg"
                    >
                      {closing.secondaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
